import { FC, Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { ThumbsUp as Like } from 'lucide-react'
import clsx from 'clsx'
import { toAbsoluteUrl } from '../../helpers'
import { Link } from 'react-router-dom'
import usePermission from '../../../Utils/usePermission'
import { useIntl } from 'react-intl'
import { FeatureGuard } from '../../../Utils/featureComponents'
import { useAuthStore } from '../../../store/authStore'
import {
  useChatDetail,
  useChatMessages,
  useChatsForSubject,
  useChatList,
  useChatStream,
  useSendMessage,
  useCreateChat,
  useSendFeedback,
  useDecideApprovals,
  usePatchSession,
  useActivePatchSessions,
} from '../../../hooks/queries'
import PatchSessionPanel from './PatchSessionPanel'
import ApprovalCard from './ApprovalCard'
import ArtifactCard from './ArtifactCard'
import { fromMessageBlocks, fromStreamApprovals } from './approvalState'
import { isArtifactBlock } from '../../../app/modules/types/chatBot'
import type { ApprovalDecision } from '../../../services/api/chat.api'
import MessageBubble from './MessageBubble'
import AssistantRow from './AssistantRow'
import AnswerBubble from './AnswerBubble'
import TurnActivity from './TurnActivity'
import { traceFromTurn } from './chatTraceStore'
import TypingIndicator from './TypingIndicator'
import { useChatTurn } from './useChatTurn'
import ChatEmptyState from './ChatEmptyState'
import ChatStartState from './ChatStartState'
import ChatComposer from './ChatComposer'
import ChatError from './ChatError'
import { useFreeChat } from './useFreeChat'
import './chat.scss'

type Props = {
  isDrawer?: boolean
}

/**
 * Which card raised an approval error, and what to say about it.
 *
 * Keyed rather than global because the message belongs BESIDE the card it came
 * from: "you do not have permission to approve this" floating at the bottom of
 * a transcript, three screens away from the request it refers to, is barely
 * better than the silence it replaces. `/approvals` is exempt from the global
 * error toast AND from the global 403 redirect (§14 and `SetupAxios.tsx`), so
 * this component is the only thing that will ever surface these.
 */
type ApprovalError = { cardKey: string; text: string }

/** The key the LIVE (not yet persisted) turn's approval card is filed under. */
const LIVE_CARD_KEY = 'live'

/**
 * How far back to look for a resumable free chat.
 *
 * There is no server-side "free chats only" filter — `?subject_type=&subject_id=`
 * is a 400 unless BOTH are given — so this reads one page of the org's newest
 * chats and takes the first row with no subject. A free chat older than this
 * page is not resumed; the user starts a new one, which is the same outcome as
 * having none.
 */
const FREE_CHAT_LOOKUP_LIMIT = 20

/**
 * The agent's tools that change a patch session's server-side status. When one
 * of these settles on the chat stream, the read-only patch card is stale and
 * must re-read the session — the manual mutations that used to keep it fresh are
 * gone (the agent owns start / connectivity-check / end now). The SSH tools
 * (`write_file`, `run_command`, …) are deliberately excluded: they operate
 * inside a session without changing its status, so refreshing on them would be
 * a request per keystroke of the agent's work for nothing that shows.
 */
const PATCH_STATUS_TOOL_NAMES = new Set([
  'start_patch_session',
  'check_patch_connectivity',
  'end_patch_session',
])

/** The error slot under one approval card. Empty for every other card. */
const ApprovalErrorNote: FC<{ error: ApprovalError | null; cardKey: string }> = ({
  error,
  cardKey,
}) =>
  error?.cardKey === cardKey ? (
    <div className='lum-error' role='alert'>
      <span>{error.text}</span>
    </div>
  ) : null

const ChatInner: FC<Props> = ({ isDrawer = false }) => {
  const targetRef = useRef<HTMLDivElement | null>(null)
  const [questionId, setQuestionId] = useState<string>('')
  const [lastQuestion, setLastQuestion] = useState<string>('')
  const [approvalError, setApprovalError] = useState<ApprovalError | null>(null)
  /** The card whose batch is in flight, so only IT greys out. */
  const [pendingCardKey, setPendingCardKey] = useState<string | null>(null)

  const { isPermission } = usePermission()
  const intl = useIntl()
  // Try direct access to selectedVulnId without the deprecated 'data' property
  const vuln_id = useAuthStore((state) => state?.selectedVulnId)
  const me = useAuthStore((state) => state.user)

  const vulnerabilitiesData = useAuthStore(
    (state) => state.aiAssistant
  )
  const vulnerabilities = useMemo(() => (vulnerabilitiesData[vuln_id]), [vuln_id, vulnerabilitiesData])
  const chatId = vulnerabilities?.id

  /**
   * Which entry point opened this. It governs ONLY the no-chat-bound case: once
   * a `chatId` exists a free chat and a finding chat render identically.
   */
  const isFreeMode = useAuthStore((state) => state.aiEntryPoint === 'free')
  const { startNewChat, bindChat, isCreating: isCreatingFreeChat } = useFreeChat()

  /**
   * The newest chat with no subject, to resume when the floating button is
   * opened with nothing bound. Disabled outside that exact case, so a bound
   * chat never issues the request at all.
   */
  const { data: recentChats } = useChatList(
    { limit: FREE_CHAT_LOOKUP_LIMIT },
    isFreeMode && !chatId,
  )
  const resumableFreeChat = useMemo(
    () => recentChats?.find((row) => row.subject_type === null) ?? null,
    [recentChats],
  )

  useEffect(() => {
    if (!isFreeMode || chatId || !resumableFreeChat) return
    bindChat(resumableFreeChat)
  }, [isFreeMode, chatId, resumableFreeChat, bindChat])

  /**
   * Is the finding→chat lookup still deciding whether this finding HAS a chat?
   *
   * Binding is a request now, not a field read off the loaded detail (§7). So
   * `useFindingChatBinding` writes `{vulID, id: null}` as soon as a finding is
   * picked and corrects `id` when `GET /chat?subject_type=finding&subject_id=…`
   * answers. During that window this component sees a `vulID` with no `chatId` —
   * indistinguishable, from here, from a finding that genuinely has no chat, so
   * it renders "Start Chat". The service dropped Django's one-per-finding
   * constraint, and `useChatsForSubject` reads `items[0]` of a `limit=1` list,
   * so a click in that window does not just make a redundant chat: it makes the
   * EARLIER conversation permanently unreachable from the finding page.
   *
   * `isLoading`, not `isFetching`. In react-query 5.90 `isLoading` is
   * `isPending && isFetching` — true only when there is no answer yet. Every
   * background refetch sets `isFetching` again, including on a finding whose
   * lookup already returned `[]`, so gating on that would blank the one button
   * on the screen at intervals for a chat-less finding.
   *
   * The subject id goes null once a chat is bound, which disables the query
   * rather than merely ignoring it. That is what keeps `ChatHistoryPage` out of
   * this: it keys FREE chats by the chat's own id, which is not a finding id, so
   * an always-enabled lookup would issue a request that can only answer `[]` —
   * and would report `isLoading` on a page that must never gate. A disabled
   * query reports `isLoading === false`, so the gate is inert there.
   *
   * While it IS enabled the arguments match `useFindingChatBinding`'s exactly,
   * so both observers share `chat.bySubject(org, 'finding', findingId)` and only
   * one request is made.
   */
  const { isLoading: isResolvingBinding } = useChatsForSubject(
    'finding',
    chatId ? null : vulnerabilities?.vulID,
  )

  // React Query hooks
  const { data: messages, refetch: refetchChatList } = useChatMessages(chatId)
  const { data: chat } = useChatDetail(chatId)
  const { streaming, connectionState, error: streamError } = useChatStream(chatId ?? null)
  const sendMessageMutation = useSendMessage()
  const createChatMutation = useCreateChat()
  const sendFeedbackMutation = useSendFeedback()
  const decideApprovals = useDecideApprovals()

  const { refresh: refreshPatchSession, sessionError } = usePatchSession(chatId)
  /**
   * Every live session the CALLER can reach, across every chat — the source
   * for the panel below, and for resolving an approval card's `session_id`
   * into the host/username/port a human actually recognises. Separate from
   * `usePatchSession(chatId)` above on purpose: that hook's read and its 403
   * are the owner gate (see `patchForbidden`), and stay wired exactly as they
   * are.
   */
  const {
    sessions: patchSessions,
    targetOf,
    refresh: refreshActivePatchSessions,
  } = useActivePatchSessions()

  /**
   * Everything about the outstanding turn — see `useChatTurn`. It is a hook
   * rather than inline state because none of these transitions was reachable
   * without a full DOM render, and the bugs all lived in the transitions.
   */
  const {
    liveTurn,
    showLiveTurn,
    showLiveTurnDots,
    showTyping,
    isSending,
    hasError,
    beginTurn,
    noteSent,
    failTurn,
  } = useChatTurn({
    chatId,
    messages,
    streaming,
    streamError,
    mutationPending: sendMessageMutation.isPending,
  })

  /**
   * Approvals are owner-only: a same-org colleague may read every message here
   * and still gets a 403 on the decision, because the resumed run acts under the
   * OWNER's identity — their SSH session, their scan quota (§9, §12).
   *
   * `me.id` and `chat.user_id` are both strings (`UserModel.id` was corrected in
   * task 1; it used to be declared `number`, which made this comparison false
   * for everyone including the owner).
   *
   * `ownershipKnown` is threaded separately because `isOwner` alone cannot say
   * "not yet": a pending — or permanently FAILED — `GET /chat/{id}` would
   * otherwise render as a flat denial aimed at the actual owner. Same shape as
   * `patchOwnershipAllows` below, and for the same reason.
   */
  const ownershipKnown = chat !== undefined && !!me?.id
  const isOwner = ownershipKnown && !!chat?.user_id && me?.id === chat.user_id

  const chatList = useMemo(() => messages ?? [], [messages])

  // Track the chat ID to detect when it changes
  const prevChatIdRef = useRef<string | null | undefined>(null)

  // When chat ID changes, reset the composer and refetch immediately.
  // The turn machine resets itself — see `useChatTurn`.
  useEffect(() => {
    if (chatId !== prevChatIdRef.current) {
      prevChatIdRef.current = chatId
      // The composer/state belongs to the previous chat — clear it on switch.
      setQuestionId('')
      setLastQuestion('')
      setApprovalError(null)
      setPendingCardKey(null)
      if (chatId) {
        refetchChatList()
      }
    }
  }, [chatId, refetchChatList])

  // Handle sending a message. One composer, one endpoint (§2): there is no mode
  // to resolve and no second send path.
  const sendMessage = (text?: string) => {
    const content = (text ?? questionId).trim()
    if (chatId && content) {
      setLastQuestion(content)
      beginTurn()
      setQuestionId('')

      sendMessageMutation.mutate(
        { chatId, content },
        {
          // The 202 carries the PERSISTED user message (§8). Its id is what the
          // answer is then measured against — a message count could not tell
          // "the assistant replied" from "history finally loaded".
          onSuccess: (persisted) => noteSent(persisted.id),
          onError: () => failTurn(),
        },
      )
    }
  }

  /**
   * Post one card's whole batch of decisions (§12).
   *
   * Nothing else will report a failure here: task 3 exempted `/approvals` from
   * the global error toast and from the global forbidden-redirect, precisely so
   * a colleague opening an owner's chat is not ejected to the dashboard. So this
   * handler is the entire error surface.
   *
   * 409 and 404 are deliberately silent. 409 means the call was already
   * decided — a double click, or a redelivered request — and 404 means the card
   * is stale; in both cases the mutation's `onSettled` refetches the message
   * list and the re-render IS the correct feedback. An error banner would
   * contradict the card that is about to appear beside it (§14).
   */
  const submitDecisions = (cardKey: string, decisions: ApprovalDecision[]) => {
    if (!chatId) return
    setApprovalError(null)
    setPendingCardKey(cardKey)
    decideApprovals.mutate(
      { chatId, decisions },
      {
        onSettled: () => setPendingCardKey(null),
        onError: (error) => {
          const status = error.response?.status
          if (status === 409 || status === 404) return
          // The two 403s mean different things and need different copy (§14).
          // `isOwner` is the discriminator: the ownership 403 is only reachable
          // when it is false, and the permission 403 (`api:patcher`/`edit` or
          // `api:chatbot`/`add`) only when it is true — the buttons that raised
          // this are not rendered for a non-owner in the first place.
          const id =
            status === 403
              ? isOwner
                ? 'CHAT.APPROVAL.ERROR_NO_PERMISSION'
                : 'CHAT.APPROVAL.ERROR_NOT_OWNER'
              : 'CHAT.APPROVAL.ERROR_GENERIC'
          setApprovalError({ cardKey, text: intl.formatMessage({ id }) })
        },
      },
    )
  }

  // Handle sending feedback. `useSendFeedback` write-throughs the returned chat
  // into the detail cache, so the thumbs render their own result — there is
  // nothing to toast and nothing to track locally.
  const sendFeedback = (feedback: boolean) => {
    if (chatId) {
      sendFeedbackMutation.mutate({ chatId, isHelpful: feedback })
    }
  }

  // Handle creating a new chat
  const createNewChat = async () => {
    if (vulnerabilities?.vulID) {
      createChatMutation.mutate(
        { subject_type: 'finding', subject_id: vulnerabilities.vulID },
        {
          onSuccess: (data) => {
            useAuthStore.getState().SetAiAssistantVulnerabilitiesID({
              vulID: vulnerabilities.vulID,
              id: data.id,
              title: vulnerabilities.title,
            })
          },
        },
      )
    }
  }

  /**
   * The live turn's approval card, shown ONLY while the live turn itself is.
   *
   * The persisted `approval_request` block is authoritative (§12 step 3): it is
   * written in the same transaction as the assistant message, before the event
   * fires, so it survives a reload. This is the stand-in for the window between
   * the event arriving and that refetch landing — and because it is tied to
   * `showLiveTurn`, which goes false the moment the persisted message lands, the
   * same turn can never render two cards.
   */
  const liveCard =
    showLiveTurn && liveTurn ? fromStreamApprovals(liveTurn.pendingApprovals, targetOf) : null

  /**
   * The in-flight turn's trace. Built from the live turn rather than read out of
   * `chatTraceStore`, which holds RESOLVED turns keyed by the message that
   * answered them — this one has no message yet.
   */
  const liveTrace = liveTurn ? traceFromTurn(liveTurn) : null

  /**
   * The session READ was refused, so this chat is not the caller's: §14 says a
   * 403 on any patch-session endpoint means "read-only view, no card".
   *
   * This is the load-bearing half of the owner gate, and the reason it is the
   * 403 rather than the ownership comparison below: `patchSessionApi.get` maps
   * only 404 to `null`, so a refused read leaves `session` null with
   * `sessionLoading` false — indistinguishable, from here, from a chat that
   * simply has no session yet. Every other status is deliberately excluded: a
   * 500 is the server having a bad day, not a statement about who owns the chat.
   */
  const patchForbidden = sessionError?.response?.status === 403
  /**
   * …and the second half: the chat row's own owner, when we have it. It is only
   * a latency optimisation over the 403 — both signals come from requests fired
   * on the same mount — but it means a known non-owner never sees the card at
   * all, rather than seeing it withdrawn a round trip later.
   *
   * Deliberately fail-OPEN on `!ownershipKnown`, which is the opposite of
   * `ApprovalCard`'s `canDecide = ownershipKnown && isOwner`. The asymmetry is
   * in what proves ownership: nothing passively tells the card who owns the
   * chat, so a control it offers on a guess can only be disproved by a 403 — it
   * must fail closed. Here the passive proof IS in flight (`GET …/patch-session`),
   * and `patchForbidden` consumes it, so uncertainty is short-lived and
   * self-correcting. The card is read-only besides, so the worst a brief
   * fail-open shows a non-owner is a status line and a public key, not an action.
   */
  const patchOwnershipAllows = !ownershipKnown || isOwner
  /**
   * The read-only patch card renders whenever the panel has something to draw
   * and the owner gate allows — in any chat, free or finding-anchored. There
   * is no manual entry point to gate on a subject any more: the agent opens a
   * session with an approval-gated tool, and this card only reports what
   * already exists.
   *
   * Gated on `patchSessions`, the caller's WHOLE listing, rather than on this
   * chat's own session (`usePatchSession(chatId)`'s read): a session opened in
   * another conversation is still usable from here, so a chat whose only
   * sessions live elsewhere must still show the card. The owner gate is
   * unaffected — it is still `patchForbidden`/`patchOwnershipAllows`, both
   * read off THIS chat's own `GET …/patch-session`, exactly as before this
   * task.
   */
  const showPatchCard = !!chatId && patchSessions.length > 0 && !patchForbidden && patchOwnershipAllows
  // A session still waiting on its key is the one thing here the user must act
  // on (install the key), and the card is the last element in the scroll body —
  // so it takes the scroll target when no turn content is competing for it.
  // Derived from `patchSessions`, the listing the panel actually renders, not
  // from this chat's own session: the caller's one pending session can be the
  // one they opened elsewhere, and the panel shows it here regardless.
  const patchCardIsWaiting =
    showPatchCard && patchSessions.some((s) => s.status === 'pending_authorization')
  // Whatever renders last in the scroll area owns the auto-scroll target.
  const showsTrailingContent = showLiveTurn || showTyping || hasError || patchCardIsWaiting

  /**
   * A stable signature of the patch-status tools that have SETTLED on the live
   * turn — one entry per settled call, so it changes exactly when the agent
   * finishes a start / connectivity-check / end, and not on every stream delta.
   * A running tool is excluded: the session's status only moves once the tool
   * has a result.
   */
  const settledPatchToolSignature = useMemo(
    () =>
      (liveTurn?.tools ?? [])
        .filter((tool) => PATCH_STATUS_TOOL_NAMES.has(tool.name) && tool.status !== 'running')
        .map((tool) => `${tool.id}:${tool.status}`)
        .join('|'),
    [liveTurn?.tools],
  )

  /**
   * Re-read the session — AND the caller's whole listing — when the agent's
   * patch tools change it. The manual mutations used to refresh the card on
   * success; with them gone this effect is what keeps both in step with the
   * server. Both calls are needed: `refreshPatchSession` only ever touches
   * THIS chat's row, and react-query's key prefix matching does not reach
   * `useActivePatchSessions`'s cache entry from it, so a session started or
   * ended here would otherwise sit stale in the listing — the exact gap that
   * left the panel showing nothing for up to ten minutes after `start_patch_session`.
   *
   * Keyed on the settled-tool signature so it fires once per settlement, plus on
   * `liveTurn.done` as a safety net — a session can be started AND ended inside a
   * single approved turn, and the completion is the last chance to reconcile.
   * Guarded so it does not fire on the initial mount, where there is neither
   * settled patch activity nor a completed turn to reconcile against.
   */
  useEffect(() => {
    if (!chatId) return
    if (!settledPatchToolSignature && !liveTurn?.done) return
    refreshPatchSession()
    refreshActivePatchSessions()
  }, [
    chatId,
    settledPatchToolSignature,
    liveTurn?.done,
    refreshPatchSession,
    refreshActivePatchSessions,
  ])

  useEffect(() => {
    if (targetRef.current) {
      targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [
    chatList,
    liveTurn?.text,
    liveTurn?.tools,
    // Reasoning is the only thing moving on a turn that streams no answer at
    // all, which is a large share of them — without this the view sits still
    // for the whole turn.
    liveTurn?.thinking,
    liveTurn?.done,
    hasError,
    // `patchCardIsWaiting`, not this chat's own session status: it is the
    // flag that actually decides whether the patch card takes `targetRef`
    // below, and it now derives from the listing, which can change on a
    // session opened in a different conversation.
    patchCardIsWaiting,
  ])

  return (
    /*
     * NO `dir` here — deliberately. This element used to pin `dir='ltr'`, which
     * overrode the document direction for the whole chat subtree: in the
     * Persian UI every static label, button and empty-state line inside the
     * chat came out left-to-right while the app around it was right-to-left.
     *
     * Inheriting from `<html>` (written by `AppRoutes` from the locale) is
     * right rather than re-deriving the direction from `I18nContext` here: a
     * second source could only agree with the first or drift from it.
     *
     * Mirroring is safe because `chat.scss` is written in logical properties
     * throughout — the flex rows, the avatar side and the composer all flip on
     * their own. The exceptions are handled where they occur: the bubble tails
     * are mirrored by explicit `[dir='rtl']` rules, and the runs that must stay
     * left-to-right whatever the locale (SSH commands, hosts, the patch key,
     * tool-argument JSON) pin `dir='ltr'` on themselves.
     */
    <div
      className={clsx(
        'position-relative d-flex flex-column w-100 h-100 overflow-hidden lum-chat',
        // Off `patchSessions` (the listing), not this chat's own session: a
        // session live only in another conversation is still one the caller
        // can act on from here, and the tint says "a live SSH session exists",
        // not "this particular chat has one".
        { 'lum-chat--patch': patchSessions.some((s) => s.status === 'connected') },
      )}
    >
      {isPermission()}
      <div
        className={clsx('d-flex flex-column px-4 pt-4 scroll-y', { 'h-100 h-lg-auto': !isDrawer })}
        style={{ flex: 1 }}
      >
        {chatId && connectionState === 'disconnected' && (
          <div className='mb-2 text-muted lum-reconnecting small'>
            {intl.formatMessage({ id: 'Reconnecting…' })}
          </div>
        )}

        {chatId &&
          chatList.map((message, index: number) => {
            const cards = fromMessageBlocks(message.blocks, targetOf)
            // Unlike approval blocks, an artifact needs no normalisation
            // between a live and a persisted shape — it only ever arrives
            // persisted, once the sandbox tool that produced it has actually
            // finished — so it is filtered straight off the raw blocks.
            const artifacts = message.blocks?.blocks?.filter(isArtifactBlock) ?? []
            return (
              <div
                key={message.id ?? `message${index}`}
                className='d-flex flex-column gap-3 mb-10'
                ref={index === chatList.length - 1 && !showsTrailingContent ? targetRef : null}
              >
                {/*
                  The bubble renders nothing when the message has no text, so an
                  empty assistant row is skipped — but the cards below are its
                  SIBLINGS, so a message whose only content is an approval
                  request still renders that request.
                */}
                <MessageBubble message={message} />
                {artifacts.map((artifact) => (
                  <ArtifactCard key={artifact.artifact_id} block={artifact} />
                ))}
                {cards.map((card, cardIndex) => {
                  // One key per BLOCK, not per message: a message carrying two
                  // cards must report its pending state and its error against
                  // each independently.
                  const cardKey = `${message.id}#${cardIndex}`
                  return (
                    <Fragment key={cardKey}>
                      <ApprovalCard
                        card={card}
                        isOwner={isOwner}
                        ownershipKnown={ownershipKnown}
                        onSubmit={(decisions) => submitDecisions(cardKey, decisions)}
                        isPending={decideApprovals.isPending && pendingCardKey === cardKey}
                      />
                      <ApprovalErrorNote error={approvalError} cardKey={cardKey} />
                    </Fragment>
                  )
                })}
              </div>
            )
          })}

        {chatId && chatList.length === 0 && !showsTrailingContent && (
          <ChatEmptyState onPrompt={(text) => sendMessage(text)} />
        )}

        {showLiveTurn && liveTurn && (
          <div className='d-flex flex-column gap-3 mb-10' ref={!hasError ? targetRef : null}>
            {/*
              ONE assistant row. The activity timeline, the answer and the
              loading cue all share one body — the cue used to be a SIBLING row,
              so a streaming answer rendered as two AI messages: the text, and
              an empty bubble under it holding the dots.
            */}
            <AssistantRow>
              {liveTrace && (
                <TurnActivity
                  trace={liveTrace}
                  isLive={showLiveTurnDots}
                  hasAnswer={!!liveTurn.text}
                />
              )}
              {liveTurn.text ? (
                <AnswerBubble content={liveTurn.text} streaming={showLiveTurnDots} />
              ) : null}
              {showLiveTurnDots && !liveTurn.text ? <TypingIndicator /> : null}
            </AssistantRow>
            {liveCard && (
              <>
                <ApprovalCard
                  card={liveCard}
                  isOwner={isOwner}
                  ownershipKnown={ownershipKnown}
                  onSubmit={(decisions) => submitDecisions(LIVE_CARD_KEY, decisions)}
                  isPending={decideApprovals.isPending && pendingCardKey === LIVE_CARD_KEY}
                />
                <ApprovalErrorNote error={approvalError} cardKey={LIVE_CARD_KEY} />
              </>
            )}
          </div>
        )}

        {/* The same shell, so nothing shifts when the first event lands. */}
        {showTyping && (
          <div className='mb-10' ref={!hasError ? targetRef : null}>
            <AssistantRow>
              <TypingIndicator />
            </AssistantRow>
          </div>
        )}

        {hasError && (
          <div className='mb-10' ref={targetRef}>
            {/*
              No question in hand means no retry to offer. That is the ordinary
              case on reopen: backfill replays the last turn's `error` event on
              every mount, long after the message that caused it was sent.
            */}
            <ChatError
              onRetry={lastQuestion ? () => sendMessage(lastQuestion) : undefined}
              detail={streamError ?? undefined}
            />
          </div>
        )}

        {/* Held back until the lookup has answered — see `isResolvingBinding`. */}
        {vulnerabilities?.vulID && !chatId && !isResolvingBinding && (
          <FeatureGuard featureId='vulnerabilities_chat_create'>
            <ChatStartState onStart={createNewChat} isPending={createChatMutation.isPending} />
          </FeatureGuard>
        )}

        {/*
          Opened from the floating button with nothing to resume. Creation is
          always an explicit click, never a side effect of opening the drawer,
          so an idle visit leaves no orphan row in the org's history.
        */}
        {isFreeMode && !chatId && (
          <FeatureGuard featureId='vulnerabilities_chat_create'>
            <ChatStartState
              variant='free'
              onStart={() => startNewChat()}
              isPending={isCreatingFreeChat}
            />
          </FeatureGuard>
        )}

        {!isFreeMode && !vulnerabilities?.vulID && !chatId && (
          <div className='lum-empty'>
            <div className='lum-orb-lg'>✦</div>
            {/*
              One id carrying the WHOLE sentence, with the link as a rich-text
              tag. It used to be `formatMessage('First select the') + ' ' +
              <Link>{formatMessage('Vulnerability')}</Link>`, which hard-codes
              English word order into the markup: Persian puts the verb last, so
              the correct sentence is "ابتدا یک ‹آسیب‌پذیری› انتخاب کنید" and
              the link belongs in the MIDDLE. Concatenation cannot express that,
              and a translator handed two fragments has no way to move one
              relative to the other — so `fa` rendered a sentence that read
              wrongly rather than merely untranslated.
            */}
            <p className='lum-empty-sub'>
              {intl.formatMessage(
                { id: 'CHAT.EMPTY.SELECT_FINDING' },
                { link: (chunks) => <Link to='vulnerabilities'>{chunks}</Link> },
              )}
            </p>
          </div>
        )}

        {/*
          Read-only status for every live session the caller can reach, not
          just this chat's — one opened elsewhere is still usable from here.
          The agent owns the lifecycle — starting, connectivity-checking and
          ending are its approval-gated tools — so there is nothing to toggle
          and nothing to submit; the panel only reports what already exists,
          and (while a session waits on its key) shows the public key a human
          has to install. `refreshPatchSession` and `refreshActivePatchSessions`
          above both fire from the same settled-patch-tool effect: the listing
          is a SEPARATE cache entry from this chat's own session read, so it
          needed its own explicit invalidation call — react-query's key prefix
          matching does not reach it from the detail key, and nothing else
          ever called it, which is why the panel used to sit stale for up to
          ten minutes after `start_patch_session`.
        */}
        {showPatchCard && (
          <FeatureGuard featureId='vulnerabilities_chat_patch'>
            <div
              className='lum-patch-card mb-4'
              ref={patchCardIsWaiting && !showLiveTurn && !hasError ? targetRef : null}
            >
              <PatchSessionPanel sessions={patchSessions} currentChatId={chatId as string} />
            </div>
          </FeatureGuard>
        )}
      </div>

      {chatId && (
        <div
          className='px-4 py-3 lum-footer'
          id={isDrawer ? 'kt_drawer_chat_messenger_footer' : 'kt_chat_messenger_footer'}
        >
          <FeatureGuard featureId='vulnerabilities_chat_feedback'>
            {/*
              `is_helpful` is readable on the chat now (§6), and `useSendFeedback`
              writes the server's own row back into the detail cache — so the
              control renders its own result instead of firing a toast into the
              void. Buttons rather than clickable divs: these are the only
              keyboard-reachable way to answer, and `aria-pressed` is what makes
              the current answer audible. The group carries the question the two
              icons are an answer to, which nothing on screen states.
            */}
            <div
              className='lum-feedback'
              role='group'
              aria-label={intl.formatMessage({ id: 'CHAT.FEEDBACK.QUESTION' })}
            >
              <button
                type='button'
                className={clsx('lum-feedback-btn pointer', {
                  'lum-feedback-btn--on': chat?.is_helpful === true,
                })}
                aria-pressed={chat?.is_helpful === true}
                aria-label={intl.formatMessage({ id: 'CHAT.FEEDBACK.HELPFUL' })}
                disabled={sendFeedbackMutation.isPending}
                onClick={() => {
                  sendFeedback(true)
                }}
              >
                <Like size={20} aria-hidden='true' className='svg-icon-2x svg-icon-success' />
              </button>
              <button
                type='button'
                className={clsx('lum-feedback-btn pointer rotate_180', {
                  'lum-feedback-btn--on': chat?.is_helpful === false,
                })}
                aria-pressed={chat?.is_helpful === false}
                aria-label={intl.formatMessage({ id: 'CHAT.FEEDBACK.NOT_HELPFUL' })}
                disabled={sendFeedbackMutation.isPending}
                onClick={() => {
                  sendFeedback(false)
                }}
              >
                <Like size={20} aria-hidden='true' className='svg-icon-2x svg-icon-danger' />
              </button>
            </div>
          </FeatureGuard>
          <FeatureGuard featureId='vulnerabilities_chat_create'>
            <ChatComposer
              value={questionId}
              onChange={setQuestionId}
              onSend={() => sendMessage()}
              disabled={isSending}
            />
          </FeatureGuard>
        </div>
      )}
    </div>
  )
}

export { ChatInner }
